import { apiFetch, csrfHeaders } from './client'

export interface UserProfile {
  uuid: string
  email: string
  first_name: string
  last_name: string
  company: string | null
  phone: string | null
  vat_number: string | null
  kvk_number: string | null
  locale: string | null
  roles?: string[]
}

// The backend wraps every response in `{ data: ... }`. Unwrap here so
// consumers get the UserProfile directly — the header + AdminLayout
// both read `user.roles` and without the unwrap they'd see undefined
// (admin guard fails even for ROLE_ADMIN users).
export async function fetchProfile(): Promise<UserProfile> {
  const res = await apiFetch<{ data: UserProfile }>('/auth/me')
  return res.data
}

export async function updateProfile(body: {
  company?: string
  phone?: string
  vatNumber?: string
  firstName?: string
  lastName?: string
  kvkNumber?: string
  locale?: string
}): Promise<UserProfile> {
  const res = await apiFetch<{ data: UserProfile }>('/auth/me', {
    method: 'PUT',
    body: JSON.stringify(body),
  })
  return res.data
}

/**
 * BFF login (WEB-408): hand off to the Vike/Node BFF, which runs the OIDC flow
 * and sets the httpOnly session cookie. `/auth/login` is a BFF route (same
 * origin as the SPA, NOT under /api/v1 — that proxies to Symfony). We pass the
 * path to return to; the BFF bounces the browser back there once authenticated.
 * No tokens, no PKCE in the browser.
 */
export function login(returnTo?: string): void {
  const path =
    returnTo ??
    (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/')
  window.location.href = `/auth/login?return=${encodeURIComponent(path)}`
}

/**
 * BFF logout: POST to the BFF `/auth/logout` route (not /api/v1). The BFF
 * deletes the Redis session and clears the cookies. CSRF header required.
 */
export async function logout(): Promise<void> {
  await fetch('/auth/logout', {
    method: 'POST',
    credentials: 'include',
    headers: csrfHeaders(),
  }).catch(() => {})
}
